import Table from '@/components/table/Table';
import {
    DISPPLAY_MOMENT_YMD_HM,
    DEFAULT_SELECT_PAGE_SIZES,
    DISPPLAY_MOMENT_YMD_15H
} from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import { WaitingPaymentOrder } from '@/types/dashboard';
import { formatDateTime } from '@/utils/datetime';
import { getLinkAccountDetail, getLinkBikeDetail, getLinkLocationDetail, getLinkOrderDetail } from '@/utils/utils';
import { TableState, Updater } from '@tanstack/react-table';
import { ColumnDef } from '@tanstack/react-table';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default observer(function WaitingPayment() {
    // hooks
    const { t } = useTranslation();

    // store
    const {
        dashboardStore: {
            waitingPaymentOrders,
            pagingWaitingPaymentOrders,
            totalWaitingPaymentOrders,
            getWaitingPayments,
            cleanWaitingPayments
        }
    } = useStore();

    // life cycle
    useEffect(() => {
        getWaitingPayments();

        return () => {
            cleanWaitingPayments();
        };
    }, []);

    // functions
    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging =
            stateUpdater instanceof Function ?
                stateUpdater(pagingWaitingPaymentOrders) :
                stateUpdater;
        getWaitingPayments({}, newPaging);
    };

    // columns
    const columns: ColumnDef<WaitingPaymentOrder>[] = [
        {
            accessorKey: 'orderCodeSystem',
            header: t('field.reservation_code'),
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <span>
                    {original.orderCodeSystem?.length > 0 && (
                        <Link
                            to={getLinkOrderDetail(original.id)}
                            target='_blank'
                            className='link-normal'
                        >
                            {original.orderCodeSystem}
                        </Link>
                    )}
                </span>
            )
        },
        {
            accessorKey: 'location',
            header: t('field.location_name'),
            size: 250,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[100px] line-clamp-2'>
                    {original.location?.name?.length > 0 && (
                        <Link
                            to={getLinkLocationDetail(original.location.id)}
                            target='_blank'
                            className='link-normal'
                        >
                            {original.location?.name}
                        </Link>
                    )}
                </div>
            )
        },
        {
            accessorKey: 'paymentDueDate',
            header: t('field.payment_deadline'),
            size: 200,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='whitespace-nowrap'>
                    {formatDateTime(original.paymentDueDate, DISPPLAY_MOMENT_YMD_15H, { useLocalTime: false })}
                </div>
            )
        },
        {
            accessorKey: 'rentalFrom',
            header: t('field.rental_date_and_time'),
            size: 150,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='whitespace-nowrap'>
                    {formatDateTime(original.rentalFrom, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false })}
                </div>
            )
        },
        {
            accessorKey: 'rentalTo',
            header: t('field.return_date_and_time'),
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='whitespace-nowrap'>
                    {formatDateTime(original.rentalTo, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false })}
                </div>
            )
        },
        {
            accessorKey: 'bike',
            header: t('field.model_bike'),
            size: 200,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[100px] line-clamp-2'>
                    {original.bike?.bikeModel?.length > 0 && (
                        <Link
                            to={getLinkBikeDetail(original.bike.id)}
                            target='_blank'
                            className='link-normal'
                        >
                            {original.bike?.bikeModel}
                        </Link>
                    )}
                </div>
            )
        },
        {
            accessorKey: 'renter',
            header: t('field.customer_name'),
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[90px] line-clamp-2'>
                    {original.renter?.fullName?.length > 0 && (
                        <Link
                            to={getLinkAccountDetail(original.renter.id)}
                            target='_blank'
                            className='link-normal'
                        >
                            {original.renter?.fullName}
                        </Link>
                    )}
                </div>
            )
        },
        {
            accessorKey: 'owner',
            header: t('field.owner'),
            size: 100,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[90px] line-clamp-2'>
                    {original.owner?.fullName?.length > 0 && (
                        <Link
                            to={getLinkAccountDetail(original.owner.id)}
                            target='_blank'
                            className='link-normal'
                        >
                            {original.owner?.fullName}
                        </Link>
                    )}
                </div>
            )
        }
    ];

    return (
        <div className='layout-card'>
            <h2 className='text-fs-mid24 leading-lh-mid24 font-medium mb-8'>
                入金待ちの予約一覧
            </h2>
            <Table
                columns={columns}
                data={waitingPaymentOrders}
                state={pagingWaitingPaymentOrders}
                rowCount={totalWaitingPaymentOrders}
                onStateChange={onStateChange}
                selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                getRowId={row => row.id?.toString()}
                showSelectPageSize={false}
            />
        </div>
    );
});
